import { NgTemplateOutlet } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { RouterLink } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import { fileToBase64, filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ConnectedOverlayDirective,
  DialogService,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconButtonComponent,
  IconComponent,
  NotificationsController,
  OverlayOriginDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { profileSelectors } from '../../../../features/profile/data/store/profile.store';
import type { ReferralsPortalProfile } from '../../../../features/profile/data/types';
import { ProgramEntityActions } from '../../../../features/program/data/store/program.store';

@Component({
  selector: 'rtr-header-logo',
  templateUrl: './header-logo.component.html',
  styleUrls: ['./header-logo.component.less'],
  imports: [
    RouterLink,
    TruncateComponent,
    NgTemplateOutlet,
    IconButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconComponent,
    TranslatePipe,
  ],
})
export class HeaderLogoComponent implements OnInit, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationsController = inject(NotificationsController);
  private readonly actions$ = inject(Actions);
  @Input() public logoUrl: string;
  @Input() public companyName: string;

  public isAdmin: boolean = false;
  public logoEditOpen: boolean = false;
  public uploading: boolean = false;
  public deleting: boolean = false;
  public canEdit: boolean = false;
  public DropdownWidth: typeof DropdownWidth = DropdownWidth;

  public profile$: Observable<ReferralsPortalProfile | null> = this.store.select(
    profileSelectors.data,
  );

  private ngOnDestroy$ = new Subject<void>();

  private successMessage: { create: string; update: string; delete: string } = {
    create: this.i18n.translate('rtr.homepage.logo.create_success_notification_message'),
    update: this.i18n.translate('rtr.homepage.logo.update_success_notification_message'),
    delete: this.i18n.translate('rtr.homepage.logo.delete_success_notification_message'),
  };

  public ngOnInit(): void {
    this.profile$
      .pipe(
        filterBoolean,
        map(profile => profile.isAdmin),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(isAdmin => {
        this.isAdmin = isAdmin;
        this.cdRef.detectChanges();
      });

    this.store
      .select(profileSelectors.abilitiesHasOne(Ability.ManageReferrals))
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(canManageReferrals => {
        this.canEdit = canManageReferrals;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleLogoEdit(): void {
    this.logoEditOpen = !this.logoEditOpen;
    this.cdRef.detectChanges();
  }

  public upload(files: FileList | null, fileInput: any): void {
    if (files && files.length > 0) {
      this.uploading = true;
      this.logoEditOpen = true;
      this.cdRef.detectChanges();

      const hasLogo = <boolean>!!this.logoUrl;

      fileToBase64(files[0])
        .pipe(take(1), takeUntil(this.ngOnDestroy$))
        .subscribe(base64 =>
          this.store.dispatch(ProgramEntityActions.updateProgram({ payload: { logo: base64 } })),
        );

      this.actions$
        .pipe(ofType(ProgramEntityActions.updateProgramSuccess), take(1))
        .subscribe(_ => {
          this.uploading = false;
          this.logoEditOpen = false;
          this.notificationsController.success(
            hasLogo ? this.successMessage.update : this.successMessage.create,
          );
          this.cdRef.detectChanges();
        });
    }
    fileInput.value = null;
  }

  public delete(): void {
    const title = this.i18n.translate('rtr.header.logo_delete.title');
    const message = this.i18n.translate('rtr.header.logo_delete.description');
    this.deleting = true;
    this.logoEditOpen = true;
    this.cdRef.detectChanges();
    this.dialogService.confirm({ title, message, variant: 'error' }).subscribe(confirmed => {
      if (confirmed) {
        this.deleting = false;
        this.logoEditOpen = false;
        this.cdRef.detectChanges();
        this.store.dispatch(ProgramEntityActions.updateProgram({ payload: { logo: null } }));
        this.notificationsController.success(this.successMessage.delete);
      }
    });
  }
}
